import {
  Box,
  FlexAnchor,
  FlexAnchorProps,
  Icon,
  PrimaryButton,
  PrimaryButtonProps,
  smBumps,
  TertiaryButton,
  TertiaryButtonProps,
  Typo,
  xxsBumps,
} from '@wrisk/ui-components'
import React, { FC, MouseEvent, useCallback } from 'react'

import { CoverOption, Quote } from '../../../domain'
import {
  tCovers,
  TKey,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { useOptionalCoverModal } from './useOptionalCoverModal'

export interface OptionalFlexAnchorProps
  extends Omit<FlexAnchorProps, 'onChange' | 'ref'> {
  coverOption: CoverOption
  loading?: boolean
  quote: Quote
  onChange?: (priceOption: CoverOption) => void
}

const tKey = TKey('components.optional-flex-anchor')

export const OptionalFlexAnchor: FC<OptionalFlexAnchorProps> = ({
  coverOption,
  loading,
  quote,
  onChange,
  ...props
}) => {
  const showActionButton = coverOption.available && onChange
  const icon = coverOption.active ? 'tick' : 'cross'
  const Button: FC<PrimaryButtonProps | TertiaryButtonProps> = coverOption.active
    ? TertiaryButton
    : PrimaryButton
  const actionTKey = coverOption.active ? 'remove' : 'add'

  const { t } = useWriskTranslation()

  const handleModalClick = useOptionalCoverModal(quote)

  const handleActionClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      onChange?.(coverOption)
    },
    [coverOption, onChange],
  )

  return (
    <FlexAnchor
      columnGap={smBumps}
      justifyContent='flex-start'
      onClick={() => handleModalClick(coverOption)}
      px={smBumps}
      py={3}
      variant='highlight'
      {...props}
    >
      <Icon icon={icon} />
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        flexGrow={showActionButton ? 0 : 1}
        gap={xxsBumps}
      >
        <Typo>{t(tCovers(coverOption.coverGroup, 'name'))}</Typo>
        <Icon icon='info' size='small' />
      </Box>
      {showActionButton && (
        <Button
          ml='auto'
          layout='small'
          loading={loading}
          onClick={handleActionClick}
          data-testid={`button-optional-${coverOption.coverGroup}-${actionTKey}`}
        >
          {t(tKey('actions', actionTKey))}
        </Button>
      )}
    </FlexAnchor>
  )
}
